// /Users/adrian.leineweber/Development/gemini-test/client/src/components/LocationDialog.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, List, ListItem,
    ListItemText, IconButton, Paper, Box, Typography, Snackbar, Alert,
    AppBar, Toolbar, useMediaQuery, CircularProgress // <-- HIER HINZUGEFÜGT
} from '@mui/material';
import { useTheme } from '@mui/material/styles'; // Hinzugefügt
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save'; // Hinzugefügt
import { LoadingButton } from '@mui/lab'; // Hinzugefügt

import { useLocations } from '../hooks/useLocations';

function LocationDialog({ open, onClose }) {
  const { locations, isLoading, error, fetchLocations, addLocation, updateLocation, deleteLocation } = useLocations();
  const [newLocationData, setNewLocationData] = useState({ name: '', adresse: '', raum: '', regal: '', fach: '' });
  const [editLocationId, setEditLocationId] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const theme = useTheme(); // Hinzugefügt
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Hinzugefügt

  useEffect(() => {
    if (open) {
      fetchLocations();
      setNewLocationData({ name: '', adresse: '', raum: '', regal: '', fach: '' });
      setEditLocationId(null);
      setLocalError(null);
    }
  }, [open, fetchLocations]);

  useEffect(() => {
      if (error) { showSnackbar(`Fehler: ${error}`, 'error'); }
  }, [error]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewLocationData(prev => ({ ...prev, [name]: value }));
  };

  const showSnackbar = useCallback((message, severity = 'info') => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
  }, []);

  const handleAddOrUpdateLocation = useCallback(async () => {
    setLocalError(null);
    if (!newLocationData.name) {
        setLocalError("Name ist erforderlich.");
        showSnackbar("Name ist erforderlich.", "warning");
        return;
    }

    let success = false;
    if (editLocationId) {
      success = await updateLocation(editLocationId, newLocationData);
      if (success) {
          showSnackbar('Lagerort erfolgreich aktualisiert.', 'success');
          setEditLocationId(null);
          setNewLocationData({ name: '', adresse: '', raum: '', regal: '', fach: '' });
      } else {
          showSnackbar('Fehler beim Aktualisieren des Lagerorts.', 'error');
      }
    } else {
      success = await addLocation(newLocationData);
       if (success) {
           showSnackbar('Lagerort erfolgreich hinzugefügt.', 'success');
           setNewLocationData({ name: '', adresse: '', raum: '', regal: '', fach: '' });
       } else {
           showSnackbar('Fehler beim Hinzufügen des Lagerorts.', 'error');
       }
    }
  }, [editLocationId, newLocationData, updateLocation, addLocation, showSnackbar]);

  const handleEditClick = useCallback((location) => {
    setEditLocationId(location._id);
    setNewLocationData(location);
  }, []);

  const handleDeleteClick = useCallback(async (id) => {
      const success = await deleteLocation(id);
       if (success) {
           showSnackbar('Lagerort erfolgreich gelöscht.', 'success');
       } else {
           showSnackbar('Fehler beim Löschen des Lagerorts.', 'error');
       }
  }, [deleteLocation, showSnackbar]);

  const handleCancelEdit = useCallback(() => {
      setEditLocationId(null);
      setNewLocationData({ name: '', adresse: '', raum: '', regal: '', fach: '' });
      setLocalError(null);
  }, []);

  const handleSnackbarClose = useCallback((event, reason) => {
      if (reason === 'clickaway') { return; }
      setSnackbarOpen(false);
  }, []);

  const isActionLoading = isLoading; // Vereinfacht

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} fullWidth maxWidth="sm">
      {/* Mobile Header */}
      {isMobile && (
          <AppBar sx={{ position: 'relative' }}>
              <Toolbar>
                  <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close" disabled={isActionLoading}>
                      <CloseIcon />
                  </IconButton>
                  <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                      Lagerorte verwalten
                  </Typography>
                  {/* Optional: Save Button im Header? Eher nicht, da Aktionen unten sind */}
                  <Button color="inherit" onClick={onClose} disabled={isActionLoading}>
                      Schließen
                  </Button>
              </Toolbar>
          </AppBar>
      )}
      {/* Desktop Title */}
      {!isMobile && (
          <DialogTitle sx={{ m: 0, p: 2, bgcolor: 'primary.main', color: 'white' }}>
              Lagerorte verwalten
              <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }} disabled={isActionLoading}>
                  <CloseIcon />
              </IconButton>
          </DialogTitle>
      )}

      <DialogContent sx={isMobile ? { pt: 2 } : {}}>
        {isLoading && !locations.length && <Typography>Lade Lagerorte...</Typography>}
        {localError && <Typography color="error" sx={{ mb: 2 }}>{localError}</Typography>}

        {/* Formular */}
        <Grid container spacing={2} sx={{ opacity: isActionLoading ? 0.7 : 1 }}>
          <Grid item xs={12}>
            <TextField label="Name" name="name" value={newLocationData.name} onChange={handleInputChange} fullWidth margin="dense" variant="outlined" required error={!!localError && !newLocationData.name} disabled={isActionLoading} />
            <TextField label="Adresse" name="adresse" value={newLocationData.adresse} onChange={handleInputChange} fullWidth margin="dense" variant="outlined" disabled={isActionLoading} />
            <TextField label="Raum" name="raum" value={newLocationData.raum} onChange={handleInputChange} fullWidth margin="dense" variant="outlined" disabled={isActionLoading} />
            <TextField label="Regal" name="regal" value={newLocationData.regal} onChange={handleInputChange} fullWidth margin="dense" variant="outlined" disabled={isActionLoading} />
            <TextField label="Fach" name="fach" value={newLocationData.fach} onChange={handleInputChange} fullWidth margin="dense" variant="outlined" disabled={isActionLoading} />
          </Grid>
          <Grid item xs={12}>
            <LoadingButton
                variant="contained"
                color="primary"
                onClick={handleAddOrUpdateLocation}
                loading={isActionLoading && (editLocationId ? true : true)} // Zeige Loading beim Speichern/Hinzufügen
                loadingPosition="start"
                startIcon={<SaveIcon />}
                disabled={isActionLoading}
            >
              {editLocationId ? 'Aktualisieren' : 'Hinzufügen'}
            </LoadingButton>
            {editLocationId && (
                <Button variant="outlined" onClick={handleCancelEdit} sx={{ ml: 1 }} disabled={isActionLoading}>
                    Abbrechen
                </Button>
            )}
          </Grid>

          {/* Liste */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>Bestehende Orte</Typography>
            <List sx={{ maxHeight: 300, overflow: 'auto' }}> {/* Scrollbar für Liste */}
              {locations.map(location => (
                <Paper key={location._id} elevation={1} sx={{ mb: 1 }}>
                  <ListItem
                    secondaryAction={
                      <Box>
                        <IconButton edge="end" aria-label="edit" onClick={() => handleEditClick(location)} disabled={isActionLoading}>
                          <EditIcon />
                        </IconButton>
<IconButton edge="end" aria-label="delete" onClick={() => handleDeleteClick(location._id)} disabled={isActionLoading}>
  {/* Hier wird CircularProgress verwendet */}
  {isLoading && editLocationId !== location._id ? <CircularProgress size={20} /> : <DeleteIcon />}
</IconButton>
                      </Box>
                    }
                  >
                    <ListItemText
                        primary={location.name}
                        secondary={`Adresse: ${location.adresse || '-'}, Raum: ${location.raum || '-'}, Regal: ${location.regal || '-'}, Fach: ${location.fach || '-'}`}
                        sx={{ pr: '80px' }} // Platz für Buttons
                    />
                  </ListItem>
                </Paper>
              ))}
              {locations.length === 0 && !isLoading && <Typography>Keine Lagerorte gefunden.</Typography>}
            </List>
          </Grid>
        </Grid>
      </DialogContent>
      {/* Actions nur auf Desktop anzeigen */}
      {!isMobile && (
          <DialogActions>
            <Button onClick={onClose} disabled={isActionLoading}>Schließen</Button>
          </DialogActions>
      )}

      {/* Snackbar */}
       <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default LocationDialog;
